/**
 * Books and book files CRUD operations.
 */

import { generateId } from '../utils';
import { ensureDB, getDB, getAll, getByBookId, getById, put, remove, addPendingChange, now } from './helpers';
import { reportError } from '../error-reporter';

/**
 * iOS WebKit (Safari, plus Chrome/Firefox on iOS since they're forced to
 * WebKit) throws `UnknownError: Cannot inject key into script value` from
 * IndexedDB **reads** when a stored record's keyPath value is malformed.
 * WebKit re-injects the key on read, and a corrupted record (id missing,
 * non-primitive, truncated after a partial write) makes that re-injection
 * fail.
 *
 * The first-discovered victim was zoe.qi@outlook.com (Max plan, iPad
 * Chrome → WebKit IDB) on the library page: useBookCovers + sync paths
 * fan out reads against book_files, hit one bad record, the throw kills
 * the whole sweep. Containment goal: log the offending bookId so we can
 * surgically clean it up, and let the rest of the library keep working
 * (return null for the bad record instead of propagating the throw).
 */
// Dedup backend reports: the §F17 sync path re-reads book_files every ~30s, so a
// single corrupt record would otherwise spam error_logs forever (zoe.qi: 163
// reports in 2.5 days from ~34 records). Report each (store,bookId) once per
// session; still console.warn each time for local debugging. The self-heal
// deletes the record, so it stops recurring within a sync or two anyway — this
// guards the window and any heal that fails. Resets on page reload.
const reportedCorruptKeys = new Set();

function reportIdbCorruption(storeName, bookId, err, fn) {
    const msg = `IDB corrupt record in ${storeName} for bookId=${bookId} (${fn}): ${err?.message || String(err)}`;
    console.warn('[idb-corrupt]', msg);
    const dedupeKey = `${storeName}:${bookId}`;
    if (reportedCorruptKeys.has(dedupeKey)) return;
    reportedCorruptKeys.add(dedupeKey);
    reportError(msg, {
        level: 'warn',
        stack: err?.stack,
        metadata: {
            source: 'idb-corrupt',
            store: storeName,
            bookId: String(bookId),
            fn,
            errName: err?.name,
        },
    });
}

/**
 * Self-heal a corrupt `book_files` record (the iOS-WebKit "Cannot inject key
 * into script value" case). A corrupt record is UNREADABLE — safeGet already
 * returned the fallback and the book can never be opened from it — so it holds
 * no recoverable value. DELETE it unconditionally:
 *   - cloud-backed book → next open re-downloads a clean blob (saveBookFile
 *     overwrites by keyPath id), recovering the book with no user action
 *   - local-only book   → the blob was already unreadable on THIS device; the
 *     books-store entry survives so the user can re-add it
 *
 * deleteBookFile is a delete-by-KEY (it never deserializes the stored value), so
 * it succeeds even on the exact record WebKit refuses to read. We deliberately
 * do NOT read the `books` store first:
 *   (a) curated books store a NULL local file_url until sync backfills it, so the
 *       old `book.file_url` guard mis-classified cloud books as local-only and
 *       left them recurring forever (zoe.qi: ~34 records, ~every 30s for days);
 *   (b) the books-store record may itself be among the corrupted ones, so the
 *       heal must not depend on reading it.
 *
 * Fire-and-forget + idempotent. The corruption was already reported (deduped).
 * Exported for unit testing.
 */
export async function selfHealCorruptBookFile(bookId) {
    try {
        await deleteBookFile(bookId);
        console.warn('[idb-corrupt] self-healed: deleted corrupt book_files record', bookId);
    } catch {
        // Best-effort — the corruption was already reported. Never throw.
    }
}

/**
 * Pattern: a one-record IDB get that:
 *   - Catches sync throws from the transaction/store.get() call itself
 *     (rare; happens on certain key-type violations).
 *   - Catches async throws inside request.onsuccess when accessing the
 *     deserialized result (iOS WebKit's key-inject failure path).
 *   - Reports corruption to error_logs with the bookId + caller context,
 *     then resolves with `fallback` (default null) so callers degrade
 *     cleanly instead of crashing the whole library scan.
 *
 * `project` runs against `request.result` — keep it cheap; any throw
 * gets caught and reported as corruption.
 */
function safeGet(storeName, bookId, project, fn, fallback = null) {
    return new Promise((resolve) => {
        let request;
        try {
            const db = getDB();
            const tx = db.transaction(storeName, 'readonly');
            request = tx.objectStore(storeName).get(bookId);
        } catch (e) {
            reportIdbCorruption(storeName, bookId, e, `${fn}/sync`);
            resolve(fallback);
            return;
        }
        request.onsuccess = () => {
            try {
                resolve(project(request.result));
            } catch (e) {
                reportIdbCorruption(storeName, bookId, e, `${fn}/result`);
                resolve(fallback);
                // Corrupt record detected on read → self-heal (delete if a cloud
                // copy exists) so it stops recurring and re-downloads clean.
                if (storeName === 'book_files') selfHealCorruptBookFile(bookId);
            }
        };
        request.onerror = () => {
            reportIdbCorruption(storeName, bookId, request.error, `${fn}/onerror`);
            resolve(fallback);
            if (storeName === 'book_files') selfHealCorruptBookFile(bookId);
        };
    });
}

// =====================================================
// Book Files (binary storage)
// =====================================================

/**
 * Persist a downloaded EPUB blob.
 * @param {string} bookId
 * @param {ArrayBuffer} arrayBuffer
 * @param {{ file_url?: string|null, epub_version?: number|null }} [meta]
 *   - file_url: R2 path the blob was downloaded from. Pinned to the blob
 *     so sync can detect curated-book replacements (path changed) even
 *     after the books-store entry is overwritten.
 *   - epub_version (§F17): server-side version counter at download time.
 *     Worker publish() bumps it on every R2 overwrite (incl. partial
 *     publishes during PDF conversion). syncService + Library compare
 *     cached vs server → evict on mismatch so reader doesn't keep
 *     serving a partial-stage EPUB.
 */
export async function saveBookFile(bookId, arrayBuffer, meta = {}) {
    if (!(await ensureDB())) throw new Error('Database unavailable');
    const db = getDB();
    return new Promise((resolve, reject) => {
        const tx = db.transaction('book_files', 'readwrite');
        const store = tx.objectStore('book_files');
        const record = {
            id: bookId,
            data: arrayBuffer,
            saved_at: now(),
            file_url: meta.file_url ?? null,
            epub_version: meta.epub_version ?? null,
        };
        const request = store.put(record);

        request.onsuccess = () => {
            resolve();
            // Bound local IDB growth so we stay under iOS WebKit's storage quota
            // (over-quota records get truncated → "Cannot inject key" corruption).
            // Fire-and-forget; protects the just-saved book from eviction.
            enforceBookFileCacheCap(bookId).catch(() => {});
        };
        request.onerror = () => reject(request.error);
    });
}

export async function getBookFile(bookId) {
    if (!(await ensureDB())) return null;
    return safeGet('book_files', bookId, r => r?.data || null, 'getBookFile');
}

/**
 * Read the file_url that was attached to the stored blob (if any).
 * Returns null if no record OR record predates this field (older blobs that
 * were saved before this metadata was added).
 */
export async function getBookFileSourceUrl(bookId) {
    if (!(await ensureDB())) return null;
    return safeGet('book_files', bookId, r => r?.file_url ?? null, 'getBookFileSourceUrl');
}

/**
 * §F17: read the epub_version pinned to the cached blob. Used by
 * syncService and Library.handleOpenBook to decide whether the cached
 * EPUB is stale relative to the server's latest version.
 *
 * Returns null when:
 *   - no cached blob exists for this book
 *   - the blob was saved before §F17 (older record with no epub_version field)
 * Caller should treat null as "unknown — don't evict on this signal alone"
 * (the curated/PDF-magic-byte evict paths still work for those cases).
 */
export async function getBookFileEpubVersion(bookId) {
    if (!(await ensureDB())) return null;
    return safeGet('book_files', bookId, r => r?.epub_version ?? null, 'getBookFileEpubVersion');
}

/**
 * Check which book IDs have local files in IndexedDB.
 * Returns a Set of bookIds that have files stored locally.
 */
export async function getLocalFileBookIds() {
    if (!(await ensureDB())) return new Set();
    const db = getDB();
    return new Promise((resolve, reject) => {
        const tx = db.transaction('book_files', 'readonly');
        const store = tx.objectStore('book_files');
        const request = store.getAllKeys();

        request.onsuccess = () => resolve(new Set(request.result || []));
        request.onerror = () => reject(request.error);
    });
}

export async function deleteBookFile(bookId) {
    if (!(await ensureDB())) return;
    const db = getDB();
    return new Promise((resolve, reject) => {
        const tx = db.transaction('book_files', 'readwrite');
        const store = tx.objectStore('book_files');
        const request = store.delete(bookId);

        request.onsuccess = () => resolve();
        request.onerror = () => reject(request.error);
    });
}

// ── Local cache cap ─────────────────────────────────────────
// Root prevention for the iOS-WebKit IndexedDB corruption: WebKit truncates
// records under storage-quota pressure, so we keep total cached book-file bytes
// bounded. We cap tightly on iOS (the danger zone); elsewhere (huge desktop
// quotas) we only bound pathological growth. (zoe.qi hit corruption at ~429 MB
// of cached EPUB blobs, incl. a 114 MB book.)

function isWebKitIOS() {
    if (typeof navigator === 'undefined') return false;
    const ua = navigator.userAgent || '';
    // iOS forces every browser onto WebKit. iPadOS 13+ reports as desktop
    // Safari ("MacIntel" + touch), so catch that too.
    return /iPad|iPhone|iPod/.test(ua)
        || (navigator.platform === 'MacIntel' && (navigator.maxTouchPoints || 0) > 1);
}

function bookFileCacheCapBytes() {
    return isWebKitIOS() ? 250 * 1024 * 1024 : 1536 * 1024 * 1024; // 250 MB on iOS, 1.5 GB elsewhere
}

/**
 * Evict least-recently-read CLOUD-BACKED book blobs when total cached bytes
 * exceed the cap. Cloud-backed books re-download on next open, so eviction is
 * lossless. NEVER evicted: local-only books (no re-download source) and the
 * currently-open book. Sizes come from the books-store `file_size` metadata —
 * we never read the blobs (slow, and the read is the very op that trips the
 * corruption). Fire-and-forget, best-effort, never throws.
 *
 * @param {string|null} openBookId  book to protect from eviction (the one being saved/opened)
 * @param {number} [maxBytes]       cap override (tests pass this; default per-platform)
 */
export async function enforceBookFileCacheCap(openBookId = null, maxBytes = bookFileCacheCapBytes()) {
    try {
        if (!(await ensureDB())) return;
        const cachedIds = await getLocalFileBookIds();   // getAllKeys — never reads blobs
        if (cachedIds.size === 0) return;

        const entries = [];
        let total = 0;
        for (const id of cachedIds) {
            const book = await getById('books', id).catch(() => null);
            const size = Number(book?.file_size) || 0;
            total += size;
            entries.push({ id, size, book });
        }
        if (total <= maxBytes) return;

        const lruMs = (e) => {
            const t = e.book?.last_read_at || e.book?.created_at;
            const ms = t ? Date.parse(t) : NaN;
            return Number.isNaN(ms) ? 0 : ms;            // never-read/unknown → evict first
        };
        // Evictable = orphan blob (no book row) OR cloud-backed (file_url set →
        // re-downloadable), excluding the protected book. Local-only books
        // (book row exists, file_url null) are NEVER evicted — no source to
        // re-download, so deleting would be real data loss.
        const candidates = entries
            .filter(e => e.id !== openBookId)
            .filter(e => !e.book || !!e.book.file_url)
            .sort((a, b) => lruMs(a) - lruMs(b));

        let evicted = 0, freed = 0;
        for (const e of candidates) {
            if (total <= maxBytes) break;
            await deleteBookFile(e.id);
            total -= e.size;
            freed += e.size;
            evicted++;
        }
        if (evicted) {
            console.log(`[cache-cap] evicted ${evicted} cloud-backed book blob(s), ~${Math.round(freed / 1048576)}MB freed (now ~${Math.round(total / 1048576)}MB / cap ${Math.round(maxBytes / 1048576)}MB)`);
        }
    } catch {
        // Best-effort — bounding the cache must never break a download.
    }
}

// =====================================================
// Book Covers (cached cover images)
// =====================================================

export async function saveBookCover(bookId, blob) {
    if (!(await ensureDB())) return;
    const db = getDB();
    if (!db.objectStoreNames.contains('book_covers')) return;
    return new Promise((resolve, reject) => {
        const tx = db.transaction('book_covers', 'readwrite');
        const store = tx.objectStore('book_covers');
        const request = store.put({ id: bookId, data: blob, saved_at: now() });

        request.onsuccess = () => resolve();
        request.onerror = () => reject(request.error);
    });
}

export async function getBookCover(bookId) {
    if (!(await ensureDB())) return null;
    const db = getDB();
    if (!db.objectStoreNames.contains('book_covers')) return null;
    return safeGet('book_covers', bookId, r => r || null, 'getBookCover');
}

export async function deleteBookCover(bookId) {
    if (!(await ensureDB())) return;
    const db = getDB();
    if (!db.objectStoreNames.contains('book_covers')) return;
    return new Promise((resolve, reject) => {
        const tx = db.transaction('book_covers', 'readwrite');
        const store = tx.objectStore('book_covers');
        const request = store.delete(bookId);

        request.onsuccess = () => resolve();
        request.onerror = () => reject(request.error);
    });
}

// =====================================================
// Books
// =====================================================

export async function getBooks(userId) {
    const books = await getAll('books', userId);
    return books.sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at));
}

export async function getBook(id) {
    return getById('books', id);
}

export async function createBook(userId, bookData) {
    const book = {
        id: bookData.id || generateId(),
        user_id: userId,
        title: bookData.title,
        author: bookData.author || null,
        file_name: bookData.file_name,
        file_type: bookData.file_type,
        file_size: bookData.file_size ?? null,
        file_url: bookData.file_url ?? null,
        cover_url: bookData.cover_url ?? null,
        total_word_count: bookData.total_word_count ?? null,
        language: bookData.language || null,
        language_confirmed_value: bookData.language_confirmed_value || null,
        canonical_book_id: bookData.canonical_book_id || null,
        is_guide_book: bookData.is_guide_book || false,
        isbn: bookData.isbn || null,
        // PDF uploads pass 'uploading' here so the IDB row has a truthy
        // conversion_status during the upload race window (createBook fires
        // before uploadBookToCloud's response lands). Non-PDF formats omit
        // this field; sync's COALESCE on the backend keeps them as 'ready'.
        conversion_status: bookData.conversion_status ?? null,
        last_read_at: null,
        created_at: now(),
        updated_at: now(),
        deleted_at: null,
        _pending: true, // Mark as pending sync
    };
    await put('books', book);
    await addPendingChange('books', 'create', book);
    return book;
}

export async function updateBook(id, updates) {
    const book = await getById('books', id);
    if (!book) return null;

    const updatedBook = {
        ...book,
        ...updates,
        updated_at: now(),
        _pending: true,
    };
    await put('books', updatedBook);
    await addPendingChange('books', 'update', updatedBook);
    return updatedBook;
}

/**
 * §F39: write a server-derived field onto the local book row without
 * marking it _pending or queuing a push. Used for fields that are
 * computed/owned by the backend (e.g. is_scan_only) — we mirror them
 * locally so the UI updates immediately, but the backend is the
 * source of truth and will re-send the same value on the next sync.
 */
export async function patchBookFromServer(id, updates) {
    const book = await getById('books', id);
    if (!book) return null;
    const patched = { ...book, ...updates };
    await put('books', patched);
    return patched;
}

export async function deleteBook(id, userId) {
    const book = await getById('books', id);
    if (!book) return;

    const timestamp = now();

    // Soft delete the book
    const deletedBook = { ...book, deleted_at: timestamp, updated_at: timestamp, _pending: true };
    await put('books', deletedBook);
    await addPendingChange('books', 'delete', deletedBook);

    // Soft delete related data and queue for sync
    const highlights = await getByBookId('highlights', id, userId);
    for (const h of highlights) {
        const deleted = { ...h, deleted_at: timestamp, updated_at: timestamp, _pending: true };
        await put('highlights', deleted);
        await addPendingChange('highlights', 'delete', deleted);
    }

    const notes = await getByBookId('notes', id, userId);
    for (const n of notes) {
        const deleted = { ...n, deleted_at: timestamp, updated_at: timestamp, _pending: true };
        await put('notes', deleted);
        await addPendingChange('notes', 'delete', deleted);
    }

    const bookmarks = await getByBookId('bookmarks', id, userId);
    for (const b of bookmarks) {
        const deleted = { ...b, deleted_at: timestamp, updated_at: timestamp, _pending: true };
        await put('bookmarks', deleted);
        await addPendingChange('bookmarks', 'delete', deleted);
    }
}
